import React from "react";
import { Button, Table, Tbody, Tr, Thead } from "@chakra-ui/react";
import { v4 as uuidV4 } from "uuid";
import PropTypes from "prop-types";
import { TableContainer, Th } from "@/components/ui/Table";
import ClassroomSessionScheduleRow from "./components/ClassroomSessionScheduleRow";
import { MaxStudentsMessage } from "../ClassroomEditV2/maxStudentsMessage";
import { envConfig } from "@/config";

export default class ClassroomSessionSchedule extends React.Component {
	MaxStudents = envConfig.MAX_STUDENTS;
	constructor(props) {
		super(props);
		this.handleAddSchedule = this.handleAddSchedule.bind(this);
		this.updateSchedule = this.updateSchedule.bind(this);
		this.renderSchedules = this.renderSchedules.bind(this);
		this.state = {
			newSchedules: new Set(),
			schedulesWithError: new Set(),
		};
	}

	addNewSchedule = (id) => {
		const newSchedules = new Set(this.state.newSchedules);
		newSchedules.add(id);
		this.setState({ newSchedules });
	};

	addScheduleWithError = (id, callback) => {
		const schedulesWithError = new Set(this.state.schedulesWithError);
		schedulesWithError.add(id);
		this.setState({ schedulesWithError }, callback);
	};

	removeScheduleWithError = (id, callback) => {
		const schedulesWithError = new Set(this.state.schedulesWithError);
		schedulesWithError.delete(id);
		this.setState({ schedulesWithError }, callback);
	};

	hasPeriodErrors = () => {
		return this.state.schedulesWithError.size > 0;
	};

	handleAddSchedule = () => {
		const id = uuidV4();
		const newSchedules = [
			{
				id,
			},
			...this.props.periods,
		];
		this.addNewSchedule(id);

		// Because the day is not set, grey out save button
		this.addScheduleWithError(id, () => {
			this.props.setSchedules(newSchedules, this.hasPeriodErrors());
		});
	};

	updateSchedule = (value, error) => {
		const _periods = Array.from(this.props.periods);
		const indexToUpdate = _periods.findIndex((period) => period.id === value.id);
		_periods[indexToUpdate] = value;

		// If the day is not set or there is an error with the time, grey out save button
		if (!value.day || error) {
			this.addScheduleWithError(value.id, () => {
				this.props.setSchedules(_periods, this.hasPeriodErrors());
			});
		} else {
			this.removeScheduleWithError(value.id, () => {
				this.props.setSchedules(_periods, this.hasPeriodErrors());
			});
		}

		// Change a table row will clear off-school hour error if any
		this.props.onClearModifyError();
	};

	removeSchedule = (value) => {
		const _periods = Array.from(this.props.periods);
		const removalIndex = _periods.findIndex((period) => period.id === value);
		_periods.splice(removalIndex, 1);

		this.removeScheduleWithError(value, () => {
			this.props.setSchedules(_periods, this.hasPeriodErrors());
		});

		// Delete a table row will clear off-school hour error if any
		this.props.onClearModifyError();
	};

	headers = (
		<Tr>
			<Th paddingLeft="16px">Day</Th>
			<Th paddingLeft="21px">Start Time</Th>
			<Th paddingLeft="21px">End Time</Th>
		</Tr>
	);

	daySorter = { mon: 1, tue: 2, wed: 3, thur: 4, fri: 5, sat: 6, sun: 7 };

	renderSchedules = () => {
		const periods = this.props.periods;
		if (periods === undefined || periods.length === 0) {
			return <div className="ClassroomSessionScheduler_noSchedule">There are no schedules defined.</div>;
		}

		const schedules = periods
			.sort((a, b) => {
				// If the period is new, it should be at the top
				if (this.state.newSchedules.has(a.id)) {
					return 0;
				}
				if (this.state.newSchedules.has(b.id)) {
					return 0;
				}
				return this.daySorter[a.day] - this.daySorter[b.day];
			})
			.map((period) => (
				<ClassroomSessionScheduleRow
					value={period}
					handleRemove={this.removeSchedule}
					onChange={this.updateSchedule}
					key={period.id}
					isNew={this.state.newSchedules.has(period.id)}
				/>
			));

		return (
			<TableContainer>
				<Table>
					<Thead>{this.headers}</Thead>
					<Tbody>{schedules}</Tbody>
				</Table>
			</TableContainer>
		);
	};

	renderError = () => {
		if (this.props.onClassroomModifyError) {
			return <div className="error">Unable to add schedule due to time restrictions set by your school.</div>;
		}
		return <></>;
	};

	handleClose = () => {
		this.props.onClose();
	};

	render() {
		return (
			<div className="ClassroomSessionScheduler">
				<div className="ClassroomSessionScheduler_descriptionText">
					Class schedules define when you teach a particular class. When using schedules, Classwize automatically starts the class session
					for you.
				</div>
				<MaxStudentsMessage studentCount={this.props.studentCount} maxStudents={this.MaxStudents} />
				<div className="ClassroomSessionScheduler_modalInnerTitle">
					<Button
						onClick={this.handleAddSchedule}
						isDisabled={this.props.onClassroomModifyError || (this.props.studentCount && this.props.studentCount > this.MaxStudents)}
					>
						Add Schedule
					</Button>
				</div>
				{this.renderError()}
				<div className="ClassroomSessionScheduler_scheduleContainer">{this.renderSchedules()}</div>
			</div>
		);
	}
}

ClassroomSessionSchedule.propTypes = {
	setSchedules: PropTypes.func,
	periods: PropTypes.array,
	onClose: PropTypes.func,
	onClassroomModifyError: PropTypes.bool,
	onClearModifyError: PropTypes.func,
};
